/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment, useState } from 'react'
import { orderBy } from 'lodash'
import { z } from 'zod'

import type { Criteria, EuiTableSortingType } from '@elastic/eui'
import { EuiBasicTable, EuiFlexGroup, EuiFlexItem, EuiSkeletonText, EuiSpacer } from '@elastic/eui'

import { CuiAlert } from '@modules/cui/Alert'
import {
  useListElasticsearchProjectsQuery,
  useListObservabilityProjectsQuery,
  useListSecurityProjectsQuery,
} from '@modules/project-admin-lib/hooks/list'
import type { AnyAdminProject } from '@modules/project-admin-api/types'
import type { ProjectType } from '@modules/ui-types/projects'
import { useQueryStringParams } from '@modules/utils/qs'
import { projectTypeColumn } from '@modules/project-components/ProjectList/columns'
import { useListRegionsQuery } from '@modules/cluster-admin-lib/hooks'

import AdminProjectListFilters from './Filters'
import { filterSchema } from './Filters/schema'
import { actionsColumn, createdColumn, projectNameColumn, regionColumn } from './columns'

import type { ComponentPropsWithoutRef, ReactNode } from 'react'

type Props = {
  organizationId?: string
  extraActions?: ReactNode
}

type SortField = keyof AnyAdminProject | 'metadata.created_at'
type SortKey = SortField | `-${SortField}`

const DEFAULT_PAGE_SIZE = 10
const PAGE_SIZE_OPTIONS = [DEFAULT_PAGE_SIZE, 20, 50, 0]

const sortSchema = z.object({
  sort: z
    .string()
    .transform((s) => s && s.toLowerCase())
    .default('-metadata.created_at')
    .refine((s): s is SortKey =>
      ['name', '-name', 'metadata.created_at', '-metadata.created_at'].includes(s),
    ),
})

const toSortKey = ({
  field,
  direction,
}: NonNullable<EuiTableSortingType<AnyAdminProject>['sort']>): SortKey =>
  `${direction === 'desc' ? '-' : ''}${field}`

const fromSortKey = (key: SortKey): NonNullable<EuiTableSortingType<AnyAdminProject>['sort']> => ({
  // This cast is an outright lie, remove when EUI updates to allow something
  // other than keyof T (which is supported in practice but not at the type
  // layer).
  field: key.replace(/^-/, '') as keyof AnyAdminProject,
  direction: key.startsWith('-') ? ('desc' as const) : ('asc' as const),
})

const AdminProjectList: React.FunctionComponent<Props> = ({ organizationId, extraActions }) => {
  const [pageIndex, setPageIndex] = useState(0)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)

  const { queryStringItems: queryParameters } = useQueryStringParams(filterSchema.parse)

  const {
    queryStringItems: { sort: sortKey },
    setQueryStringItem,
  } = useQueryStringParams(sortSchema.parse)

  if (organizationId) {
    // ignore QS-based org_id if we have one passed in directly
    queryParameters.organization_id = organizationId
  }

  const enabled: Record<ProjectType, boolean> = {
    elasticsearch: [undefined, 'elasticsearch'].includes(queryParameters.type),
    observability: [undefined, 'observability'].includes(queryParameters.type),
    security: [undefined, 'security'].includes(queryParameters.type),
  }

  const regionsQuery = useListRegionsQuery()
  const { data: regions, isLoading: regionsIsLoading } = regionsQuery

  const elasticsearchQuery = useListElasticsearchProjectsQuery(queryParameters, {
    enabled: enabled.elasticsearch,
  })
  const observabilityQuery = useListObservabilityProjectsQuery(queryParameters, {
    enabled: enabled.observability,
  })
  const securityQuery = useListSecurityProjectsQuery(queryParameters, {
    enabled: enabled.security,
  })

  const queries = [
    enabled.elasticsearch && elasticsearchQuery,
    enabled.observability && observabilityQuery,
    enabled.security && securityQuery,
  ].filter((v): v is Exclude<typeof v, false> => Boolean(v))

  return (
    <div>
      {renderErrors()}
      {renderFilters()}
      {renderList()}
    </div>
  )

  function renderErrors() {
    const failedQueries = queries.filter((query) => query.isError)

    if (!failedQueries.length) {
      return null
    }

    // If multiple queries failed, chances are it's a connection issue, so let's
    // not bash them over the head with three identical errors.
    return (
      <Fragment>
        <CuiAlert type='danger'>{failedQueries[0]?.error}</CuiAlert>
        <EuiSpacer size='m' />
      </Fragment>
    )
  }

  function renderFilters() {
    const hide: ComponentPropsWithoutRef<typeof AdminProjectListFilters>['hide'] = []

    if (organizationId) {
      hide.push('organization')
    }

    return (
      <Fragment>
        <EuiFlexGroup gutterSize='m'>
          <EuiFlexItem grow={true}>
            <AdminProjectListFilters hide={hide} />
          </EuiFlexItem>
          {extraActions && <EuiFlexItem grow={false}>{extraActions}</EuiFlexItem>}
        </EuiFlexGroup>
        <EuiSpacer size='m' />
      </Fragment>
    )
  }

  function renderList() {
    const isLoading = queries.some((query) => query.isLoading)

    if (isLoading || regionsIsLoading) {
      return <EuiSkeletonText />
    }

    const successfulQueries = queries.filter((query) => query.isSuccess)

    const items: AnyAdminProject[] = []

    for (const query of successfulQueries) {
      const dataItems = query.data.items

      items.push(...dataItems)
    }

    const sort = fromSortKey(sortKey)

    const sortedItems = orderBy(items, sort.field, sort.direction)

    const visibleItems = sortedItems.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize)

    const pagination = {
      pageIndex,
      pageSize,
      totalItemCount: items.length,
      pageSizeOptions: PAGE_SIZE_OPTIONS,
      showPerPageOptions: true,
    }

    return (
      <EuiBasicTable
        tableLayout='auto'
        rowHeader='name'
        items={visibleItems}
        columns={[
          projectNameColumn,
          { ...projectTypeColumn, width: '16em' },
          { ...regionColumn(regions), width: '15em' },
          { ...createdColumn, width: '15em' },
          { ...actionsColumn, width: '6em' },
        ]}
        pagination={pagination}
        sorting={{ sort }}
        onChange={onChange}
      />
    )

    function onChange({ page, sort }: Criteria<AnyAdminProject>) {
      if (page) {
        setPageIndex(page.index)
        setPageSize(page.size)
      }

      if (sort) {
        setQueryStringItem('sort', toSortKey(sort))
      }
    }
  }
}

export default AdminProjectList
